import React from 'react';
import {
    Box,
    Chip,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { dateTimeApiToUi } from '../../const/dates';

const EVENTO_LABELS = {
    creada: 'Creada',
    asignada: 'Asignada',
    turno_asignado: 'Turno asignado',
    reprogramado: 'Reprogramada',
    rechazada: 'Rechazada',
    turno_cancelado: 'Turno cancelado',
    cancelada: 'Cancelada',
    cerrada: 'Cerrada',
    destrabada: 'Destrabada',
    pendiente_manual: 'Pendiente manual',
    liberada_por_timeout: 'Liberada por plazo',
    insumo_registrado: 'Insumo registrado',
    insumo_forzado: 'Insumo forzado',
    insumo_vencido: 'Insumo vencido',
    aviso_enviado: 'Aviso enviado',
};

const MOTIVO_LABELS = {
    sin_candidatos_o_plazo_max: 'Sin candidatos o plazo máximo',
    plazo_max_sin_turno: 'Plazo máximo sin turno',
    plazo_respuesta: 'Plazo de respuesta',
};

const CIERRE_LABELS = {
    efectivizado: 'Efectivizado',
    no_asistio: 'No asistió',
    cancelado: 'Cancelado',
    otro: 'Otro',
};

const MODO_LABELS = {
    reasignar: 'Reasignar',
    turno_directo: 'Turno directo',
};

function colorForTipo(tipo) {
    if (['creada', 'asignada'].includes(tipo)) return 'info.main';
    if (['turno_asignado', 'reprogramado'].includes(tipo)) return 'primary.main';
    if (['rechazada', 'cancelada', 'liberada_por_timeout'].includes(tipo)) return 'error.main';
    if (['turno_cancelado', 'pendiente_manual', 'insumo_vencido', 'aviso_enviado'].includes(tipo)) return 'warning.main';
    if (['cerrada', 'insumo_registrado', 'insumo_forzado'].includes(tipo)) return 'success.main';
    return 'grey.500';
}

function detallesEvento(e) {
    const p = e.payload || {};
    const lines = [];

    if (e.tipo === 'reprogramado') {
        if (p.turno_en_anterior) lines.push(`Turno anterior: ${dateTimeApiToUi(p.turno_en_anterior)}`);
        if (p.turno_en) lines.push(`Turno nuevo: ${dateTimeApiToUi(p.turno_en)}`);
        if (p.reprogramacion_n != null) {
            const max = p.reprogramaciones_max != null ? p.reprogramaciones_max : '—';
            lines.push(`Reprogramación ${p.reprogramacion_n} de ${max}`);
        }
    } else if (e.tipo === 'turno_cancelado' && p.turno_en_anterior) {
        lines.push(`Turno anulado: ${dateTimeApiToUi(p.turno_en_anterior)}`);
    } else if (p.turno_en) {
        lines.push(`Turno: ${dateTimeApiToUi(p.turno_en)}`);
    }

    if (p.resultado) lines.push(`Resultado: ${CIERRE_LABELS[p.resultado] || p.resultado}`);
    if (p.origen) lines.push(`Origen: ${p.origen}`);
    if (p.modo) lines.push(`Modo: ${MODO_LABELS[p.modo] || p.modo}`);

    if (!['reprogramado', 'rechazada', 'cancelada', 'turno_cancelado'].includes(e.tipo) && p.motivo) {
        lines.push(`Motivo: ${MOTIVO_LABELS[p.motivo] || p.motivo}`);
    }

    if (e.tipo === 'aviso_enviado' && p.tipo) {
        lines.push(p.tipo === 'insumo' ? 'Aviso de insumo' : p.tipo === 'cierre' ? 'Aviso de cierre' : `Aviso: ${p.tipo}`);
    }

    return lines;
}

/**
 * Historial de auditoría de una orden.
 */
export default function OrdenHistorial({ eventos = [] }) {
    if (!eventos?.length) {
        return (
            <Paper sx={{ p: 3, width: '100%', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Historial</Typography>
                <Typography variant="body2" color="text.secondary">Sin movimientos registrados.</Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3, width: '100%', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Historial</Typography>
            <Box>
                {eventos.map((e) => {
                    const color = colorForTipo(e.tipo);
                    const detalles = detallesEvento(e);
                    return (
                        <Box
                            key={e.id}
                            sx={{
                                position: 'relative',
                                pl: 2.5,
                                pb: 2,
                                borderLeft: '2px solid',
                                borderColor: color,
                                '&:last-child': { pb: 0 },
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: -5,
                                    top: 4,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: color,
                                }}
                            />
                            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {EVENTO_LABELS[e.tipo] || e.tipo}
                                </Typography>
                                {e.payload?.forzado_admin ? (
                                    <Chip size="small" color="warning" label="Excepción de administrador" />
                                ) : null}
                                {e.tipo === 'cerrada' && e.payload?.anticipado ? (
                                    <Chip size="small" color="info" label="Anticipado" />
                                ) : null}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {dateTimeApiToUi(e.ocurrio_at)}
                                {' · '}
                                {e.usuario_nombre ? `Por ${e.usuario_nombre}` : 'Sistema'}
                                {e.establecimiento_nombre ? ` · ${e.establecimiento_nombre}` : ''}
                            </Typography>
                            {detalles.map((line) => (
                                <Typography key={line} variant="caption" color="text.secondary" display="block">
                                    {line}
                                </Typography>
                            ))}
                            {e.comentario ? (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: 0.75,
                                        pl: 1,
                                        borderLeft: '3px solid',
                                        borderColor: 'divider',
                                        fontStyle: 'italic',
                                        color: 'text.secondary',
                                    }}
                                >
                                    {e.comentario}
                                </Typography>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}
